import { ChangeDetectionStrategy, Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { MatOptionSelectionChange } from '@angular/material/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DevExtremeFieldTypesEnum } from '@views/secured/beyond-smart-reports/enums/dev-extreme-field-types.enum';
import { FilterOperationDialogEnum } from '@views/secured/beyond-smart-reports/enums/filter-operation-dialog.enum';
import { Color } from '@angular-material-components/color-picker';
import {
  DataGridObjectFilterModel
} from '@views/secured/beyond-smart-reports/views/report/components/devextreme/model/data-grid-object-filter.model';
import { ColumnModel } from '@shared/components/beyond-table/model/column.model';
import { markAllAsTouched } from '@shared/util/form.util';
import { SnackBarService } from '@base/services/snackbar.service';
import { MAT_COLOR_FORMATS_PROVIDER } from '@shared/external-modules/angular-material.module';
import {
  DialogTemplateButtonComboStyleEnum
} from '@shared/components/templates/dialog-template/enums/dialog-template-button.enum';
import { CustomFieldModel } from '@base/modules/rest/custom-field/model/custom-field.model';
import { convertDataType } from '@views/secured/beyond-smart-reports/views/report/components/devextreme/utils/data-grid-conditional-formatting';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';

interface DialogFormArrayModel {
  fieldType: FormControl<DevExtremeFieldTypesEnum>;
  fieldName: FormControl<string>;
  operation: FormControl<FilterOperationDialogEnum>;
  value: FormControl<any>;
  textColor: FormControl<Color>;
  textHighlightColor: FormControl<Color>;
  textStyle: FormControl<string>;
  textFormattingPreview: FormControl<string>;
  isRowColoring?: FormControl<boolean>;
}

interface FormattingDialogData {
  columns: ColumnModel[];
  fieldFilters: DataGridObjectFilterModel[];
  fields?: CustomFieldModel[];
}

interface DialogFormModel {
  formats: FormArray<FormGroup<DialogFormArrayModel>>;
}

export interface OptionModel<A = string | number | [], B = string, C = any> {
  value: A;
  data: B;
  dataType?: C;
}

@UntilDestroy()
@Component({
  selector: 'app-formatting-dialog',
  templateUrl: './formatting-dialog.component.html',
  styleUrls: ['./formatting-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  providers: [
    MAT_COLOR_FORMATS_PROVIDER,
  ],
})
export class FormattingDialogComponent {
  protected readonly DevExtremeFieldTypesEnum = DevExtremeFieldTypesEnum;

  form: FormGroup<DialogFormModel>;
  newData: OptionModel[] = [];
  textStyle: { data: string; value: string | number | [] }[] = [];
  styleList: OptionModel[] = [{value: 'normal', data: 'Regular'}, {value: 'Bold', data: 'Bold'}, {value: 'Italic', data: 'Italic'}, {value: 'Underline', data: 'Underline'}];
  fieldsCleared = false;
  DialogTemplateButtonComboStyleEnum = DialogTemplateButtonComboStyleEnum;

  constructor(@Inject(MAT_DIALOG_DATA) public data: FormattingDialogData,
              private dialogRef: MatDialogRef<FormattingDialogComponent, any>,
              private snackBarService: SnackBarService) {
    this.form = new FormGroup<DialogFormModel>({
      formats: new FormArray<FormGroup<DialogFormArrayModel>>([]),
    });

    this.newData = this.data.columns.map((d: ColumnModel) => {
      let dataType: string = d.dataType;
      if (this.data.fields) {
        dataType = convertDataType(this.data.fields, d.dataField);
      }
      return {
        value: d.dataField,
        data: d.caption,
        dataType: dataType,
      };
    });

    this.textStyle = this.styleList.map((d: OptionModel) => {
      return {
        value: d.value,
        data: d.data,
      };
    });

    if (this.data.fieldFilters.length > 0) {
      this.data.fieldFilters?.map((item: DataGridObjectFilterModel) => {
        const group = new FormGroup<DialogFormArrayModel>({
          fieldType: new FormControl<DevExtremeFieldTypesEnum>(item.fieldType),
          fieldName: new FormControl<string>({value: item.name, disabled: false}, Validators.required),
          operation: new FormControl<FilterOperationDialogEnum>({
            value: item.operator,
            disabled: false
          }, Validators.required),
          value: new FormControl<any>({
            value: item.value,
            disabled: false
          }, Validators.required),
          textColor: new FormControl<Color>({
            value: item.color ? new Color(item.color.r, item.color.g, item.color.b, item.color.a) : null,
            disabled: false,
          }),
          textHighlightColor: new FormControl<Color>({
            value: item.highlightColor ? new Color(item.highlightColor.r, item.highlightColor.g, item.highlightColor.b, item.highlightColor.a) : null,
            disabled: false,
          }),
          textStyle: new FormControl<string>({
            value: item.textStyle,
            disabled: false,
          }),
          textFormattingPreview: new FormControl<string>(item.textFormattingPreview),
          isRowColoring: new FormControl<boolean>({
            value: item.isRowColoring,
            disabled: false,
          }),
        });
        this.formats.push(group);
      });
    } else {
      this.generateEmptyGroups();
    }
  }

  updateProperties(e: MatAutocompleteSelectedEvent, formGroup: FormGroup<DialogFormArrayModel>): void {
    const field = this.newData.find(column => {
      // if (!e.isUserInput) {
      //   return null;
      // }
      return column.value === e.option.value;
    });

    if (field) {
      formGroup.controls.fieldType.setValue(field.dataType);
      formGroup.controls.operation.enable();
      formGroup.controls.textColor.enable();
      formGroup.controls.textHighlightColor.enable();
      formGroup.controls.textStyle.enable();
      formGroup.controls.textFormattingPreview.enable();
      if (formGroup.controls.fieldType.getRawValue() === DevExtremeFieldTypesEnum.BOOLEAN) {
        formGroup.controls.operation.setValue(FilterOperationDialogEnum.EQUALS);
      }
      formGroup.controls.value.reset();
      formGroup.controls.isRowColoring.enable();
    }
  }

  generateEmptyGroups(): void {
    const group_ = new FormGroup<DialogFormArrayModel>({
      fieldType: new FormControl<DevExtremeFieldTypesEnum>(null),
      fieldName: new FormControl<string>(null, Validators.required),
      operation: new FormControl<FilterOperationDialogEnum>({value: null, disabled: true}, Validators.required),
      value: new FormControl<any>({value: null, disabled: true}, Validators.required),
      textColor: new FormControl<Color>({
        value: null,
        disabled: false
      }),
      textHighlightColor: new FormControl<Color>({
        value: null,
        disabled: false
      }),
      textStyle: new FormControl<string>({
        value: null,
        disabled: false,
      }),
      textFormattingPreview: new FormControl<string>({
        value: null,
        disabled: false,
      }),
      isRowColoring: new FormControl<boolean>({
        value: false,
        disabled: false,
      }),
    });
    this.formats.push(group_);
  }

  get formats(): FormArray<FormGroup<DialogFormArrayModel>> {
    return this.form.get('formats') as FormArray<FormGroup<DialogFormArrayModel>>;
  }

  get formatsGroups(): FormGroup<DialogFormArrayModel>[] {
    return this.formats.controls as FormGroup<DialogFormArrayModel>[];
  }

  addFormatFormGroup(): void {
    this.generateEmptyGroups();
  }

  enableValueFormControl(i: number): void {
    const formControls = this.formatsGroups[i].controls;
    const valueFormControl = formControls.value;

    formControls.operation.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((val: FilterOperationDialogEnum) => {
        val ? valueFormControl.enable() : valueFormControl.disable();
      });
  }

  closeDialog(): void {
    this.dialogRef.close(this.data.fieldFilters);
  }

  deleteFormat(i: number): void {
    this.formats.removeAt(i);
  }

  static open(dialog: MatDialog, data: FormattingDialogData): MatDialogRef<FormattingDialogComponent> {
    const isSmallScreen = window.innerWidth < 600;

    return dialog.open(FormattingDialogComponent, {
      data,
      width: '98vw',
      panelClass: isSmallScreen ? 'dialog-fixed-height' : '',
    });
  }

  applyNewFormat(): void {
    if (!this.fieldsCleared) {
      if (!this.form.valid) {
        markAllAsTouched(this.form);
        this.snackBarService.openErrorMessage('COMMON.INVALID_FORM');
        return;
      }
    }
    const compositeFilters = this.getFilterValues();
    this.dialogRef.close(compositeFilters);
  }

  clearFilters(): void {
    this.formatsGroups.forEach((fg: FormGroup<DialogFormArrayModel>) => {
      fg.controls.fieldName.setValue(null);
      fg.controls.operation.setValue(null);
      fg.controls.value.setValue(null);
      fg.controls.textColor.setValue(null);
      fg.controls.textHighlightColor.setValue(null);
      fg.controls.textStyle.setValue(null);
      fg.controls?.isRowColoring.setValue(null);
    });
    this.formatsGroups.length = 1;
    this.fieldsCleared = true;
  }

  getFilterValues(): DataGridObjectFilterModel[] {
    let compositeFilters: DataGridObjectFilterModel[] = [];
    this.formatsGroups
      .forEach((fg: FormGroup<DialogFormArrayModel>) => {
        const item: DataGridObjectFilterModel = {
          fieldType: fg.value.fieldType,
          name: fg.getRawValue().fieldName,
          operator: fg.getRawValue().operation,
          value: fg.getRawValue().value,
          color: fg.value.textColor,
          highlightColor: fg.value.textHighlightColor,
          textStyle: fg.value.textStyle,
          isRowColoring: fg.value.isRowColoring,
        };
        compositeFilters = [...compositeFilters, item];
      });
    return compositeFilters;
  }

  getSelectOptions(index: number): OptionModel[] {
    const otherItemNames = this.formats.controls
      .filter((item, arrayIndex) => arrayIndex !== index);

    return this.newData
      .filter(option => {
        return !otherItemNames.some(otherItem => option.value === otherItem.value.fieldName);
      });
  }
}
